import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, {
  GameDetailResponse,
  GameLineupPlayerItem,
  GameTimelineEvent,
} from '@/services/communityService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Card, CardContent } from '@/components/shadcn/card';
import { toast } from 'react-hot-toast';
import { navigateBackToGameRekap } from '@/utils/hostMabarNavigation';

export type EventType =
  | 'goal'
  | 'own_goal'
  | 'save'
  | 'clearance'
  | 'key_pass'
  | 'freestyle'
  | 'foul'
  | 'yellow_card'
  | 'red_card'
  | 'substitute';

interface EventConfig {
  type: EventType;
  label: string;
  description: string;
  icon: string;
  iconBg: string;
  successTitle: string;
}

const EVENT_CONFIGS: Record<EventType, EventConfig> = {
  goal: {
    type: 'goal',
    label: 'Catat Gol',
    description: 'Gol berhasil dicetak',
    icon: 'ph:soccer-ball-bold',
    iconBg: 'bg-emerald-100 text-emerald-600',
    successTitle: 'Gol Dicatat',
  },
  own_goal: {
    type: 'own_goal',
    label: 'Gol Bunuh Diri (Own Goal)',
    description: 'Pemain lawan mencetak gol ke gawang sendiri',
    icon: 'ph:arrows-clockwise-bold',
    iconBg: 'bg-red-100 text-red-600',
    successTitle: 'Gol Bunuh Diri Dicatat',
  },
  save: {
    type: 'save',
    label: 'Penyelamatan (Save)',
    description: 'Kiper menggagalkan peluang gol',
    icon: 'ph:shield-check-bold',
    iconBg: 'bg-emerald-100 text-emerald-600',
    successTitle: 'Penyelamatan (Save) Dicatat',
  },
  clearance: {
    type: 'clearance',
    label: 'Clearance',
    description: 'Bola dibuang dari area berbahaya',
    icon: 'ph:shield-bold',
    iconBg: 'bg-blue-100 text-blue-600',
    successTitle: 'Clearance Dicatat',
  },
  key_pass: {
    type: 'key_pass',
    label: 'Key Pass',
    description: 'Umpan yang menciptakan peluang',
    icon: 'ph:arrow-elbow-down-right-bold',
    iconBg: 'bg-purple-100 text-purple-600',
    successTitle: 'Key Pass Dicatat',
  },
  freestyle: {
    type: 'freestyle',
    label: 'Freestyle',
    description: 'Aksi individu yang menghibur',
    icon: 'ph:sparkle-bold',
    iconBg: 'bg-amber-100 text-amber-600',
    successTitle: 'Freestyle Dicatat',
  },
  foul: {
    type: 'foul',
    label: 'Pelanggaran (Foul)',
    description: 'Terjadi pelanggaran ringan',
    icon: 'ph:warning-bold',
    iconBg: 'bg-amber-100 text-amber-600',
    successTitle: 'Pelanggaran (Foul) Dicatat',
  },
  yellow_card: {
    type: 'yellow_card',
    label: 'Kartu Kuning',
    description: 'Peringatan resmi dari wasit',
    icon: 'ph:rectangle-bold',
    iconBg: 'bg-amber-100 text-amber-500',
    successTitle: 'Kartu Kuning Dicatat',
  },
  red_card: {
    type: 'red_card',
    label: 'Kartu Merah',
    description: 'Pemain harus meninggalkan lapangan',
    icon: 'ph:rectangle-bold',
    iconBg: 'bg-red-100 text-red-500',
    successTitle: 'Kartu Merah Dicatat',
  },
  substitute: {
    type: 'substitute',
    label: 'Pergantian Pemain',
    description: 'Pemain masuk menggantikan',
    icon: 'ph:arrows-left-right-bold',
    iconBg: 'bg-blue-100 text-blue-600',
    successTitle: 'Pergantian Pemain Dicatat',
  },
};

const normalizeTimeline = (raw: unknown): GameTimelineEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is GameTimelineEvent =>
      e != null && typeof e === 'object' && 'time' in e && 'type' in e && 'team_side' in e
  ) as GameTimelineEvent[];
};

const MabarRekapSelectPlayerPage = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const eventTypeParam = (searchParams.get('event') as EventType) || 'freestyle';
  const initialTeam = (searchParams.get('team') as 'home' | 'away') || 'home';
  const rawTime = searchParams.get('time') || searchParams.get('timeTag') || "0'";
  const timeTag = rawTime.endsWith("'") ? rawTime : `${rawTime}'`;

  const isGoalEvent = eventTypeParam === 'goal' || eventTypeParam === 'own_goal';
  const [isOwnGoal, setIsOwnGoal] = useState<boolean>(eventTypeParam === 'own_goal');

  const eventConfig = isOwnGoal
    ? EVENT_CONFIGS.own_goal
    : (EVENT_CONFIGS[eventTypeParam] || EVENT_CONFIGS.freestyle);

  const [game, setGame] = useState<GameDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTeamTab, setActiveTeamTab] = useState<'home' | 'away'>(initialTeam);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // Modals inside Select Player Page
  const [assistModal, setAssistModal] = useState<{
    scorer: GameLineupPlayerItem;
    assistId: string;
  } | null>(null);

  const [substituteModal, setSubstituteModal] = useState<{
    playerOut: GameLineupPlayerItem;
    playerInId: number | null;
  } | null>(null);

  useEffect(() => {
    if (!gameSlug) return;
    setLoading(true);
    communityService
      .getGameBySlug(gameSlug)
      .then(setGame)
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  }, [gameSlug]);

  const timeline = useMemo(() => normalizeTimeline(game?.timeline ?? []), [game?.timeline]);

  const homeName = game?.home_team_name || 'Green Team';
  const awayName = game?.away_team_name || 'Navy Team';

  // If isOwnGoal, the score goes to activeTeamTab, but we pick an opponent player!
  const opponentTeamTab = activeTeamTab === 'home' ? 'away' : 'home';
  const targetTeamTab = isOwnGoal ? opponentTeamTab : activeTeamTab;
  const targetTeamName = targetTeamTab === 'home' ? homeName : awayName;
  const benefitingTeamName = activeTeamTab === 'home' ? homeName : awayName;

  const currentTeamName = activeTeamTab === 'home' ? homeName : awayName;

  const lineupPlayers = useMemo(() => {
    if (!game?.lineups) return [];
    return game.lineups.filter((l) => l.team_side === targetTeamTab);
  }, [game?.lineups, targetTeamTab]);

  const filteredPlayers = useMemo(() => {
    if (!searchQuery.trim()) return lineupPlayers;
    const q = searchQuery.toLowerCase();
    return lineupPlayers.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.player_number != null && String(p.player_number).includes(q)) ||
        (q === '#' && (p.player_number == null || String(p.player_number).trim() === '')) ||
        (p.position || '').toLowerCase().includes(q)
    );
  }, [lineupPlayers, searchQuery]);

  const getPositionInfo = (player: GameLineupPlayerItem) => {
    const raw = (player.position || player.role || '').toUpperCase().trim();
    if (
      raw.includes('GK')
    ) {
      return { code: 'GK', bgClass: 'bg-amber-100 text-amber-700' };
    }
    if (
      raw.includes('DF')
    ) {
      return { code: 'DF', bgClass: 'bg-blue-100 text-blue-700' };
    }
    if (
      raw.includes('FW')
    ) {
      return { code: 'FW', bgClass: 'bg-red-100 text-red-700' };
    }
    if (
      raw.includes('MD')
    ) {
      return { code: 'MD', bgClass: 'bg-emerald-100 text-emerald-700' };
    }

    if (player.player_number === 1) {
      return { code: 'GK', bgClass: 'bg-amber-100 text-amber-700' };
    }

    if (raw && raw !== 'PEMAIN' && raw !== 'PLAYER') {
      return { code: raw.slice(0, 3), bgClass: 'bg-slate-100 text-slate-700' };
    }

    return { code: 'MD', bgClass: 'bg-emerald-100 text-emerald-700' };
  };

  const eventCounts = useMemo(() => {
    const map: Record<number, number> = {};
    timeline.forEach((e) => {
      if (e.player_id) {
        if (isOwnGoal && (e.type === 'own_goal' || e.detail === 'own_goal')) {
          map[e.player_id] = (map[e.player_id] || 0) + 1;
        } else if (!isOwnGoal && eventTypeParam === 'goal' && e.type === 'goal' && e.detail !== 'own_goal') {
          map[e.player_id] = (map[e.player_id] || 0) + 1;
        } else if (e.type === eventTypeParam) {
          map[e.player_id] = (map[e.player_id] || 0) + 1;
        }
      }
    });
    return map;
  }, [timeline, eventTypeParam, isOwnGoal]);

  const handleSelectPlayer = async (player: GameLineupPlayerItem) => {
    if (saving || !gameSlug) return;

    if (isOwnGoal) {
      setSaving(true);
      try {
        const newEvent: GameTimelineEvent = {
          time: timeTag,
          type: 'own_goal',
          team_side: activeTeamTab,
          player_id: player.player_participant_id,
          detail: 'own_goal',
        };

        const nextTimeline = [...timeline, newEvent];
        await communityService.updateGameTimeline(gameSlug, nextTimeline);

        toast.success(`Gol Bunuh Diri Dicatat: ${player.name} (${targetTeamName}) — Gol untuk ${benefitingTeamName}`);
        navigateBackToGameRekap(navigate, gameSlug, 1);
      } catch (err: unknown) {
        toast.error('Gagal mencatat gol bunuh diri');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (eventTypeParam === 'goal') {
      navigate(
        `/host/mabar/game/${gameSlug}/rekap/select-assist?scorerId=${player.player_participant_id}&scorerName=${encodeURIComponent(player.name)}&team=${activeTeamTab}&timeTag=${timeTag}&seconds=${searchParams.get('seconds') || '0'}`
      );
      return;
    }

    if (eventTypeParam === 'substitute') {
      setSubstituteModal({ playerOut: player, playerInId: null });
      return;
    }

    setSaving(true);
    try {
      const newEvent: GameTimelineEvent = {
        time: timeTag,
        type: eventTypeParam,
        team_side: activeTeamTab,
        player_id: player.player_participant_id,
      };

      const nextTimeline = [...timeline, newEvent];
      await communityService.updateGameTimeline(gameSlug, nextTimeline);

      toast.success(`${eventConfig.label} Dicatat: ${player.name} (menit ${timeTag})`);
      navigateBackToGameRekap(navigate, gameSlug, 1);
    } catch (err: unknown) {
      toast.error('Gagal mencatat kejadian');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGoalWithAssist = async (scorer: GameLineupPlayerItem, assistIdStr: string) => {
    if (saving || !gameSlug) return;
    setSaving(true);
    try {
      const assistId = assistIdStr === 'none' || !assistIdStr ? null : parseInt(assistIdStr, 10);
      const newEvent: GameTimelineEvent = {
        time: timeTag,
        type: 'goal',
        team_side: activeTeamTab,
        player_id: scorer.player_participant_id,
        assist_id: assistId,
      };

      const nextTimeline = [...timeline, newEvent];
      await communityService.updateGameTimeline(gameSlug, nextTimeline);

      setAssistModal(null);
      toast.success(`Gol Dicatat: ${scorer.name} (menit ${timeTag})`);
      navigateBackToGameRekap(navigate, gameSlug, 1);
    } catch (err: unknown) {
      toast.error('Gagal menyimpan gol');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSubstitution = async (playerOut: GameLineupPlayerItem, playerInId: number) => {
    if (saving || !gameSlug) return;
    setSaving(true);
    try {
      const minuteNum = parseInt(timeTag.replace(/\D/g, ''), 10) || 0;
      await communityService.postSubstitution(gameSlug, {
        time: `${minuteNum}'`,
        team_side: activeTeamTab,
        player_id_out: playerOut.player_participant_id,
        player_id_in: playerInId,
      });

      setSubstituteModal(null);
      toast.success(`Pergantian Pemain Dicatat: ${currentTeamName} (menit ${timeTag})`);
      navigateBackToGameRekap(navigate, gameSlug, 1);
    } catch (err: unknown) {
      toast.error('Gagal memproses pergantian pemain');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 max-w-md mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 pt-5 pb-16 max-w-md mx-auto">
      {/* Desktop Top Header (Hidden on Mobile as MobileTopbar handles topbar) */}
      <div className="hidden md:flex items-center justify-between gap-3 mb-5">
        <button
          type="button"
          onClick={() => {
            if (gameSlug) navigateBackToGameRekap(navigate, gameSlug, 1);
            else navigate(-1);
          }}
          className="p-2 -ml-2 rounded-full hover:bg-gray-200 text-gray-700 transition-colors cursor-pointer"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-extrabold text-[#0E1726] truncate">{eventConfig.label}</h1>
          <p className="text-xs font-semibold text-gray-400 truncate">
            Pilih pemain · menit {timeTag}
          </p>
        </div>

        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${eventConfig.iconBg}`}>
          <Icon icon={eventConfig.icon} className="w-5 h-5" />
        </div>
      </div>

      {/* Goal Mode Switcher (Gol Biasa vs Own Goal) */}
      {isGoalEvent && (
        <div className="grid grid-cols-2 p-1 bg-gray-100 border border-gray-200 rounded-2xl mb-3 shadow-2xs">
          <button
            type="button"
            onClick={() => setIsOwnGoal(false)}
            className={`py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              !isOwnGoal
                ? 'bg-white text-emerald-600 shadow-xs border border-emerald-200'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon icon="ph:soccer-ball-bold" className="w-4 h-4" />
            <span>Gol Biasa</span>
          </button>
          <button
            type="button"
            onClick={() => setIsOwnGoal(true)}
            className={`py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              isOwnGoal
                ? 'bg-white text-red-600 shadow-xs border border-red-200'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon icon="ph:arrows-clockwise-bold" className="w-4 h-4" />
            <span>Own Goal (Gol Bunuh Diri)</span>
          </button>
        </div>
      )}

      {/* Team Selector Pills */}
      <div className="mb-3">
        <p className="text-[11px] font-bold text-gray-500 mb-1.5 px-1">
          {isOwnGoal ? 'Tim yang Mendapatkan Skor Gol (+1):' : 'Tim Pencetak Gol:'}
        </p>
        <div className="grid grid-cols-2 p-1 bg-[#F5F6F7] border border-gray-200 rounded-2xl">
          <button
            type="button"
            onClick={() => setActiveTeamTab('home')}
            className={`py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
              activeTeamTab === 'home'
                ? 'bg-white text-[#FC480D] shadow-xs border border-orange-200'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span>{homeName}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTeamTab('away')}
            className={`py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
              activeTeamTab === 'away'
                ? 'bg-white text-[#1E293B] shadow-xs border border-slate-300'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-slate-800" />
            <span>{awayName}</span>
          </button>
        </div>
      </div>

      {/* Own Goal Informational Banner */}
      {isOwnGoal && (
        <div className="mb-4 p-3.5 rounded-2xl bg-red-50/90 border border-red-200 flex items-start gap-3 shadow-2xs">
          <div className="w-8 h-8 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0 mt-0.5">
            <Icon icon="ph:warning-circle-bold" className="w-4 h-4" />
          </div>
          <div className="text-xs">
            <p className="font-extrabold text-red-900">
              Pilih Pemain Lawan yang Melakukan Own Goal
            </p>
            <p className="text-red-700 font-medium mt-0.5 leading-relaxed">
              Skor akan diberikan ke <strong>{benefitingTeamName}</strong> (+1 Gol).
            </p>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative mb-4">
        <Icon
          icon="ph:magnifying-glass-bold"
          className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Cari nama pemain ${isOwnGoal ? targetTeamName : ''}`}
          className="w-full h-11 pl-10 pr-4 rounded-2xl border border-gray-200 bg-white text-xs font-semibold text-gray-800 outline-none focus:border-[#FC480D] transition-colors"
        />
      </div>

      {/* Player List Card */}
      <Card className="rounded-3xl border border-gray-200 shadow-2xs overflow-hidden bg-white mb-4">
        <CardContent className="p-3 sm:p-4 space-y-2">
          {filteredPlayers.map((p) => {
            const playerNum =
              p.player_number != null && p.player_number !== undefined && String(p.player_number).trim() !== ''
                ? p.player_number
                : '#';
            const isBench = p.is_starting === false;
            const posInfo = getPositionInfo(p);
            const eCount = eventCounts[p.player_participant_id] || 0;

            return (
              <div
                key={p.id}
                onClick={() => handleSelectPlayer(p)}
                className={`p-3 rounded-2xl border flex items-center justify-between gap-3 transition-all cursor-pointer active:scale-98 ${
                  isOwnGoal
                    ? 'border-gray-100 hover:border-red-200 hover:bg-red-50/20'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-orange-50/20'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Jersey Icon with Number */}
                  <div className="relative w-9 h-10 flex items-center justify-center shrink-0">
                    <Icon
                      icon="ph:t-shirt-fill"
                      className={`w-9 h-9 ${isOwnGoal ? 'text-red-500' : 'text-[#FC480D]'}`}
                    />
                    <span className="absolute inset-x-0 text-center text-[11px] font-black text-white top-2.5">
                      {playerNum}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-[#0E1726] truncate">{p.name}</h4>
                    <p className="text-[10px] font-semibold text-gray-400">
                      {targetTeamName} · {isBench ? 'Bench' : 'On field'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${posInfo.bgClass}`}>
                    {posInfo.code}
                  </span>
                  {eCount > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-red-50 text-red-500 border border-red-100">
                      {eCount}x {isOwnGoal ? 'OG' : ''}
                    </span>
                  )}
                  <button
                    type="button"
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                      isOwnGoal
                        ? 'bg-red-50 text-red-600 hover:bg-red-600 hover:text-white'
                        : 'bg-orange-50 text-[#FC480D] hover:bg-[#FC480D] hover:text-white'
                    }`}
                  >
                    <Icon icon={isOwnGoal ? 'ph:warning-bold' : 'ph:plus-bold'} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}

          {filteredPlayers.length === 0 && (
            <div className="text-center py-8 text-xs text-gray-400 font-semibold">
              Pemain tidak ditemukan
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Info Hint */}
      <div className="flex items-center gap-2 text-xs text-gray-400 font-medium px-2">
        <Icon icon="ph:info-bold" className="w-4 h-4 shrink-0 text-gray-400" />
        <span>
          {isOwnGoal
            ? `Ketuk nama pemain lawan untuk mencatat gol bunuh diri (-3 MMR).`
            : `Ketuk nama pemain untuk mencatat ${eventConfig.label.toLowerCase()}.`}
        </span>
      </div>

      {/* Assist Selection Modal for Goal */}
      {assistModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
          onClick={() => setAssistModal(null)}
        >
          <div
            className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-extrabold text-[#0E1726]">Pilih Pemberi Assist</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Pencetak gol: <strong className="text-[#0E1726]">{assistModal.scorer.name}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAssistModal(null)}
                className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center"
              >
                <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {lineupPlayers.map((p) => {
                const pIdStr = String(p.player_participant_id);
                if (pIdStr === String(assistModal.scorer.player_participant_id)) return null;
                const isSelected = assistModal.assistId === pIdStr;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setAssistModal({ ...assistModal, assistId: pIdStr })}
                    className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left cursor-pointer ${
                      isSelected ? 'border-[#FC480D] bg-orange-50/50' : 'border-gray-200'
                    }`}
                  >
                    <span className="text-xs font-bold text-[#0E1726]">{p.name}</span>
                    {isSelected && <Icon icon="ph:check-bold" className="w-4 h-4 text-[#FC480D]" />}
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => setAssistModal({ ...assistModal, assistId: 'none' })}
                className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left cursor-pointer ${
                  assistModal.assistId === 'none' ? 'border-[#FC480D] bg-orange-50/50' : 'border-gray-200'
                }`}
              >
                <span className="text-xs font-bold text-[#0E1726]">Tanpa assist</span>
                {assistModal.assistId === 'none' && (
                  <Icon icon="ph:check-bold" className="w-4 h-4 text-[#FC480D]" />
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={() => handleSaveGoalWithAssist(assistModal.scorer, assistModal.assistId)}
              disabled={saving}
              className="w-full h-11 rounded-full bg-[#FC480D] text-white text-xs font-bold shadow-md cursor-pointer active:scale-95 flex items-center justify-center gap-2"
            >
              {saving ? 'Menyimpan...' : 'Simpan Gol'}
            </button>
          </div>
        </div>
      )}

      {/* Substitute Selection Modal */}
      {substituteModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
          onClick={() => setSubstituteModal(null)}
        >
          <div
            className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-extrabold text-[#0E1726]">Pilih Pemain Masuk (Bench)</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Pemain keluar: <strong className="text-[#0E1726]">{substituteModal.playerOut.name}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSubstituteModal(null)}
                className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center"
              >
                <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {lineupPlayers
                .filter((p) => p.is_starting === false)
                .map((p) => {
                  const id = p.player_participant_id;
                  const isSelected = substituteModal.playerInId === id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSubstituteModal({ ...substituteModal, playerInId: id })}
                      className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left cursor-pointer ${
                        isSelected ? 'border-[#FC480D] bg-orange-50/50' : 'border-gray-200'
                      }`}
                    >
                      <span className="text-xs font-bold text-[#0E1726]">{p.name}</span>
                      {isSelected && <Icon icon="ph:check-bold" className="w-4 h-4 text-[#FC480D]" />}
                    </button>
                  );
                })}
              {lineupPlayers.filter((p) => p.is_starting === false).length === 0 && (
                <p className="text-xs text-gray-400 italic text-center py-4">
                  Tidak ada pemain di bangku cadangan
                </p>
              )}
            </div>

            <button
              type="button"
              disabled={substituteModal.playerInId == null || saving}
              onClick={() =>
                substituteModal.playerInId != null &&
                handleSaveSubstitution(substituteModal.playerOut, substituteModal.playerInId)
              }
              className={`w-full h-11 rounded-full bg-[#FC480D] text-white text-xs font-bold shadow-md cursor-pointer active:scale-95 flex items-center justify-center gap-2 ${
                substituteModal.playerInId == null || saving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {saving ? 'Memproses...' : 'Proses Pergantian'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MabarRekapSelectPlayerPage;
